import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
const SonnerToaster = lazy(() => import("@/components/ui/sonner").then(m => ({ default: m.Toaster })));
const Toaster = lazy(() => import("@/components/ui/toaster").then(m => ({ default: m.Toaster })));
import { TooltipProvider } from "@/components/ui/tooltip";
import { LanguageProvider } from "@/contexts/LanguageContext";
import Index from "./pages/Index.tsx";
import NotFound from "./pages/NotFound.tsx";

const PracticeAreas = lazy(() => import("./pages/PracticeAreas.tsx"));
const PracticeAreaDetail = lazy(() => import("./pages/PracticeAreaDetail.tsx"));
const About = lazy(() => import("./pages/About.tsx"));
const Contact = lazy(() => import("./pages/Contact.tsx"));
const Results = lazy(() => import("./pages/Results.tsx"));
const Attorneys = lazy(() => import("./pages/Attorneys.tsx"));
const ClientPortal = lazy(() => import("./pages/ClientPortal.tsx"));
const Locations = lazy(() => import("./pages/Locations.tsx"));
const LocationDetail = lazy(() => import("./pages/LocationDetail.tsx"));
const Resources = lazy(() => import("./pages/Resources.tsx"));
const FAQHub = lazy(() => import("./pages/FAQHub.tsx"));
const StartHere = lazy(() => import("./pages/StartHere.tsx"));
const ExistingClients = lazy(() => import("./pages/ExistingClients.tsx"));
const EmergencyHelp = lazy(() => import("./pages/EmergencyHelp.tsx"));
const G28Guide = lazy(() => import("./pages/G28Guide.tsx"));
const CalculatorPage = lazy(() => import("./pages/Calculator.tsx"));
const AssetProtection = lazy(() => import("./pages/AssetProtection.tsx"));
const InjuryMalpracticeHub = lazy(() => import("./pages/InjuryMalpracticeHub.tsx"));
const InjuryClaims = lazy(() => import("./pages/InjuryClaims.tsx"));
const MedicalDefense = lazy(() => import("./pages/MedicalDefense.tsx"));
const InjuryMalpracticeSubpage = lazy(() => import("./pages/InjuryMalpracticeSubpage.tsx"));
const InjuryMalpracticeBridge = lazy(() => import("./pages/InjuryMalpracticeBridge.tsx"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy.tsx"));
const TermsOfService = lazy(() => import("./pages/TermsOfService.tsx"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe.tsx"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Suspense fallback={null}><Toaster /></Suspense>
      <Suspense fallback={null}><SonnerToaster /></Suspense>
      <BrowserRouter>
        <LanguageProvider>
          <Suspense fallback={null}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/es" element={<Index />} />

              <Route path="/practice-areas" element={<PracticeAreas />} />
              <Route path="/es/areas-de-practica" element={<PracticeAreas />} />
              <Route path="/practice-areas/:slug" element={<PracticeAreaDetail />} />
              <Route path="/es/areas-de-practica/:slug" element={<PracticeAreaDetail />} />

              <Route path="/about" element={<About />} />
              <Route path="/es/nosotros" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/es/contacto" element={<Contact />} />
              <Route path="/results" element={<Results />} />
              <Route path="/es/resultados" element={<Results />} />
              <Route path="/attorneys" element={<Attorneys />} />
              <Route path="/es/abogados" element={<Attorneys />} />
              <Route path="/client-portal" element={<ClientPortal />} />
              <Route path="/es/portal-de-clientes" element={<ClientPortal />} />

              <Route path="/locations" element={<Locations />} />
              <Route path="/es/ubicaciones" element={<Locations />} />
              <Route path="/locations/:slug" element={<LocationDetail />} />
              <Route path="/es/ubicaciones/:slug" element={<LocationDetail />} />

              <Route path="/resources" element={<Resources />} />
              <Route path="/es/recursos" element={<Resources />} />
              <Route path="/resources/g28-guide" element={<G28Guide />} />
              <Route path="/es/recursos/guia-g28" element={<G28Guide />} />
              <Route path="/resources/calculator" element={<CalculatorPage />} />
              <Route path="/es/recursos/calculadora" element={<CalculatorPage />} />

              <Route path="/faq" element={<FAQHub />} />
              <Route path="/es/preguntas-frecuentes" element={<FAQHub />} />
              <Route path="/start-here" element={<StartHere />} />
              <Route path="/es/comience-aqui" element={<StartHere />} />
              <Route path="/existing-clients" element={<ExistingClients />} />
              <Route path="/es/clientes-actuales" element={<ExistingClients />} />
              <Route path="/emergency" element={<EmergencyHelp />} />
              <Route path="/es/emergencia" element={<EmergencyHelp />} />

              <Route path="/asset-protection" element={<AssetProtection />} />
              <Route path="/es/proteccion-patrimonial" element={<AssetProtection />} />

              <Route path="/personal-injury-medical-malpractice" element={<InjuryMalpracticeHub />} />
              <Route path="/es/lesiones-negligencia-defensa" element={<InjuryMalpracticeHub />} />
              <Route path="/injury-claims" element={<InjuryClaims />} />
              <Route path="/es/reclamos-por-lesiones" element={<InjuryClaims />} />
              <Route path="/medical-defense" element={<MedicalDefense />} />
              <Route path="/es/defensa-medica" element={<MedicalDefense />} />
              <Route path="/personal-injury-medical-malpractice/doctor-complaint-or-defense" element={<InjuryMalpracticeBridge />} />
              <Route path="/es/lesiones-negligencia-defensa/queja-o-defensa-medica" element={<InjuryMalpracticeBridge />} />
              <Route path="/personal-injury-medical-malpractice/medical-malpractice-claim-or-defense" element={<InjuryMalpracticeBridge />} />
              <Route path="/es/lesiones-negligencia-defensa/reclamo-o-defensa-negligencia" element={<InjuryMalpracticeBridge />} />
              <Route path="/personal-injury-medical-malpractice/:slug" element={<InjuryMalpracticeSubpage />} />
              <Route path="/es/lesiones-negligencia-defensa/:slug" element={<InjuryMalpracticeSubpage />} />

              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/es/privacidad" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/es/terminos" element={<TermsOfService />} />
              <Route path="/unsubscribe" element={<Unsubscribe />} />

              <Route path="/es/*" element={<NotFound />} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </LanguageProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
